/**
 * In-memory + localStorage cache for Places enrichment (photo names, rating, reviews).
 * Keyed by pin id. Stores photoName(s) (not the API key); media URLs are rebuilt at read time.
 * Photo names expire — entries older than TTL are treated as misses.
 */

import {
  getGoogleMapsApiKey,
  placePhotoMediaUrl,
  type PlacePhotoResult,
  type PlaceReview,
} from "./googlePlaces";

export const PLACE_PHOTO_CACHE_KEY = "chile-trip-place-photos-v2";
/** ~6 days — photo resource names expire; refresh periodically. */
export const PLACE_PHOTO_TTL_MS = 6 * 24 * 60 * 60 * 1000;

export type CachedPlacePhoto = {
  photoName: string;
  photoNames?: string[];
  credit: string;
  placeId?: string;
  rating?: number;
  userRatingCount?: number;
  reviews?: PlaceReview[];
  fetchedAt: number;
};

type CacheMap = Record<string, CachedPlacePhoto>;

const memory: CacheMap = {};

function readStorage(): CacheMap {
  try {
    const raw = localStorage.getItem(PLACE_PHOTO_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return parsed as CacheMap;
  } catch {
    return {};
  }
}

function writeStorage(map: CacheMap) {
  try {
    localStorage.setItem(PLACE_PHOTO_CACHE_KEY, JSON.stringify(map));
  } catch {
    /* quota / private mode */
  }
}

function isFresh(entry: CachedPlacePhoto, now = Date.now()): boolean {
  return (
    typeof entry.photoName === "string" &&
    entry.photoName.length > 0 &&
    typeof entry.fetchedAt === "number" &&
    now - entry.fetchedAt < PLACE_PHOTO_TTL_MS
  );
}

function toResult(entry: CachedPlacePhoto, key: string): PlacePhotoResult {
  const photoNames =
    entry.photoNames && entry.photoNames.length > 0
      ? entry.photoNames
      : [entry.photoName];
  return {
    photoName: photoNames[0],
    photoNames,
    mediaUrl: placePhotoMediaUrl(photoNames[0], 800, key),
    mediaUrls: photoNames.map((n) => placePhotoMediaUrl(n, 800, key)),
    credit: entry.credit,
    placeId: entry.placeId,
    rating: entry.rating,
    userRatingCount: entry.userRatingCount,
    reviews: entry.reviews,
  };
}

export function getCachedPlacePhoto(pinId: string): PlacePhotoResult | null {
  const key = getGoogleMapsApiKey();
  if (!key) return null;

  const mem = memory[pinId];
  if (mem && isFresh(mem)) {
    return toResult(mem, key);
  }

  const stored = readStorage()[pinId];
  if (stored && isFresh(stored)) {
    memory[pinId] = stored;
    return toResult(stored, key);
  }

  return null;
}

export function setCachedPlacePhoto(pinId: string, result: PlacePhotoResult) {
  const entry: CachedPlacePhoto = {
    photoName: result.photoName,
    photoNames: result.photoNames,
    credit: result.credit,
    placeId: result.placeId,
    rating: result.rating,
    userRatingCount: result.userRatingCount,
    reviews: result.reviews,
    fetchedAt: Date.now(),
  };
  memory[pinId] = entry;
  writeStorage({ ...readStorage(), [pinId]: entry });
}

export function invalidateCachedPlacePhoto(pinId: string) {
  delete memory[pinId];
  const all = readStorage();
  if (pinId in all) {
    delete all[pinId];
    writeStorage(all);
  }
}

/** Test helper — clear memory + storage. */
export function clearPlacePhotoCache() {
  for (const k of Object.keys(memory)) delete memory[k];
  try {
    localStorage.removeItem(PLACE_PHOTO_CACHE_KEY);
  } catch {
    /* ignore */
  }
}
